// src/components/Reportes/MermasVenaScrap/composables/useMermasVenaScrap.js
import { ref, computed } from "vue";
import { getCatalogoMaquinas } from "@/utils/api_backend";
import {
  getVenaScrapPorMaquina,
  getVenaScrapTotalesMensuales,
} from "@/utils/api_vena_scrap";

const MESES_CORTO = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

function formatearFechaISO(fecha) {
  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");
  return `${anio}-${mes}-${dia}`;
}

function periodoMesActual() {
  const hoy = new Date();
  const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  return {
    inicio: formatearFechaISO(primerDiaMes),
    fin: formatearFechaISO(hoy),
  };
}

function etiquetaPeriodo(periodo) {
  if (!periodo?.inicio || !periodo?.fin) return "";

  const formatear = (fechaStr) => {
    const fecha = new Date(`${fechaStr}T00:00:00`);
    const dia  = String(fecha.getDate()).padStart(2, "0");
    const mes  = MESES_CORTO[fecha.getMonth()];
    const anio = String(fecha.getFullYear()).slice(-2);
    return `${dia}/${mes}/${anio}`;
  };

  return `${formatear(periodo.inicio)} - ${formatear(periodo.fin)}`;
}

export function useMermasVenaScrap() {
  const cargando = ref(false);
  const cargandoMaquinas = ref(false);
  const error = ref(null);

  // Único periodo: alimenta tanto la gráfica como la fila "actual" de la tabla de totales
  // Default: del día 1 del mes actual a hoy
  const periodoPrincipal = ref(periodoMesActual());

  // ids de catalogo-maquinas/. Vacío = todas las máquinas.
  const maquinasSeleccionadas = ref([]);
  const catalogoMaquinas = ref([]);

  const datosMaquinas = ref([]);
  // Histórico de meses ya cerrados (todo lo existente, sin contar el periodo actual)
  const historicoTotales = ref([]);

  const totalPrincipal = computed(() =>
    datosMaquinas.value.reduce(
      (acc, d) => ({
        scrap: acc.scrap + d.scrap,
        vena: acc.vena + d.vena,
        total: acc.total + d.scrap + d.vena,
      }),
      { scrap: 0, vena: 0, total: 0 }
    )
  );

  // Combina el histórico + el periodo principal actual, ordena cronológicamente
  // y calcula la variación de scrap contra el periodo inmediato anterior.
  const filasTabla = computed(() => {
    const etiquetaActual = etiquetaPeriodo(periodoPrincipal.value);
    const base = [...historicoTotales.value];

    if (etiquetaActual && !base.some((f) => f.etiqueta === etiquetaActual)) {
      base.push({
        etiqueta: etiquetaActual,
        periodo: periodoPrincipal.value,
        scrap: totalPrincipal.value.scrap,
        vena: totalPrincipal.value.vena,
        total: totalPrincipal.value.total,
      });
    }

    const ordenadas = [...base].sort(
      (a, b) => new Date(a.periodo.inicio) - new Date(b.periodo.inicio)
    );

    return ordenadas
      .map((fila, idx) => {
        const anterior = ordenadas[idx - 1];
        return {
          ...fila,
          variacionScrap: anterior ? fila.scrap - anterior.scrap : null,
          esActual: fila.etiqueta === etiquetaActual,
        };
      })
      .reverse(); // periodo más reciente primero
  });

  function paramsMaquinas() {
    return maquinasSeleccionadas.value.length ? maquinasSeleccionadas.value.join(",") : "";
  }

  async function cargarCatalogoMaquinas() {
    cargandoMaquinas.value = true;
    try {
      catalogoMaquinas.value = await getCatalogoMaquinas();
    } catch (e) {
      console.error("Error al cargar catálogo de máquinas:", e);
    } finally {
      cargandoMaquinas.value = false;
    }
  }

  async function cargarDatosPrincipal() {
    cargando.value = true;
    error.value = null;
    try {
      datosMaquinas.value = await getVenaScrapPorMaquina({
        fecha_inicio: periodoPrincipal.value.inicio,
        fecha_fin: periodoPrincipal.value.fin,
        maquinas: paramsMaquinas(),
      });
    } catch (e) {
      error.value = "No se pudieron cargar los datos del periodo seleccionado.";
      console.error("Error al cargar datos principales de Vena/Scrap:", e);
    } finally {
      cargando.value = false;
    }
  }

  async function cargarHistoricoTotales() {
    try {
      const datos = await getVenaScrapTotalesMensuales({ maquinas: paramsMaquinas() });
      historicoTotales.value = datos.map((item) => ({
        ...item,
        etiqueta: etiquetaPeriodo(item.periodo),
      }));
    } catch (e) {
      console.error("Error al cargar histórico de totales:", e);
    }
  }

  async function cargarTodo() {
    await cargarCatalogoMaquinas();
    await Promise.all([cargarDatosPrincipal(), cargarHistoricoTotales()]);
  }

  function actualizarPeriodoPrincipal(nuevoPeriodo) {
    periodoPrincipal.value = nuevoPeriodo;
    cargarDatosPrincipal();
  }

  function actualizarMaquinasSeleccionadas(nuevasMaquinas) {
    maquinasSeleccionadas.value = nuevasMaquinas;
    cargarDatosPrincipal();
    cargarHistoricoTotales();
  }

  return {
    cargando,
    cargandoMaquinas,
    error,
    periodoPrincipal,
    maquinasSeleccionadas,
    catalogoMaquinas,
    datosMaquinas,
    totalPrincipal,
    filasTabla,
    etiquetaPeriodo,
    cargarTodo,
    actualizarPeriodoPrincipal,
    actualizarMaquinasSeleccionadas,
  };
}